import type { ReactNode } from "react";
import { useCallback, useContext, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Feature, useSimpleFeatureTernary } from "@viagogo/feature";
import type { EventGridItem, MappingPagedCollectionOfEventSearchResultAndEventGridItem } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { LocationContext } from "src/shared/context/location-context";
import { useCreateQueryFn } from "src/shared/hooks/useCreateQueryFn";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsMobileClientApp } from "src/shared/utils/appContextHelper";
import { createQueryKey } from "src/shared/utils/query-helpers";
import { QueryKeyPrefix } from "src/shared/utils/query/query-key-prefixes";
import { STALE_TIME_ONE_HOUR } from "src/shared/utils/query/retry-constants";
import { DateType } from "src/types/shared/cookies";
import { EmptyCarousel } from "src/website-apps/shared/components/carousel";
import {
  CarouselId,
  CarouselItemType,
} from "src/website-apps/shared/components/carousel-module/carousel-analytics-event";
import type { CarouselSize } from "src/website-apps/shared/components/CarouselSize";
import { GridCardBase } from "src/website-apps/shared/components/grid-card-base/GridCardBase";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { ImageGridManager } from "src/website-apps/shared/components/grid/ImageGridManager";
import { ImageSize } from "src/website-apps/shared/components/image";
import { isPerformerOrGrouping } from "src/website-apps/shared/components/immersive-performer/utils";
import { InViewTracker } from "src/website-apps/shared/components/InViewTracker";
import { CAROUSEL_SIZES, HOME_CAROUSEL_IDS } from "src/website-apps/shared/components/responsive-carousel/constants";
import { ResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/ResponsiveCarousel";
import { renderWithResponsiveCarousel } from "src/website-apps/shared/components/responsive-carousel/util";
import { useContentLayoutContext } from "src/website-apps/shared/context/ContentLayoutContext";
import { DateContext } from "src/website-apps/shared/context/date-context";
import { useCarouselDimensions } from "src/website-apps/shared/hooks/useCarouselDimensions";
import { useSyncItemsWithFavoritesContext } from "src/website-apps/shared/utils/favorites-utils";
import { renderCommonEventCard } from "../../../../../shared/components/common-event-card";
import { DontMissTitleText } from "./components/DontMissTitleText";
import { DontMissCarouselGridWrapper } from "./styled-components";

interface DontMissEvents {
  variant: number;
  topLevelCategoryId: number | null;
  className?: string;
  size?: (typeof CarouselSize)[keyof typeof CarouselSize] | null;
}

/**
 * Dont Miss Deals (Last Minute Deals)
 */
export const DontMissEvents = (props: DontMissEvents) => {
  const { variant, topLevelCategoryId, className, size } = props;
  const appContext = useAppContext();
  const createQueryFn = useCreateQueryFn();
  const storePageEvent = useStorePageEvent();
  const isMobile = !useMediaQuery(Breakpoint.Tablet);
  const { pageLayoutEnabled } = useContentLayoutContext();
  const pageSize = 10;
  const { carouselSize, imageSize, gapPx } = useCarouselDimensions(size);
  const isBaseCardRefactor =
    useSimpleFeatureTernary(FeatureSetEnum.UseGridCardBaseComponentRefactor, false, true) ?? false;

  const [userLocation] = useContext(LocationContext);
  const dateContext = useContext(DateContext);
  const { lt, lg } = userLocation ?? {};
  const dates = dateContext?.[0];
  const { from, to } = dates?.dates ?? {};
  const hasUserDateFilter = dates?.type !== undefined && dates.type !== DateType.ALL_DATES;

  const {
    data,
    error,
    isLoading: pending,
  } = useQuery({
    queryKey: createQueryKey(QueryKeyPrefix.DONT_MISS_EVENTS, {
      topLevelCategoryId: topLevelCategoryId ?? 0,
      lt,
      lg,
      from,
      to,
    }),
    queryFn: createQueryFn<MappingPagedCollectionOfEventSearchResultAndEventGridItem>({
      method: "DontMissEvents",
      params: [
        ["categoryId", topLevelCategoryId ?? 0],
        ["maxRows", pageSize],
        ["page", 0],
        ...(lt !== null && lt !== undefined ? [["lt", lt] as [string, number]] : []),
        ...(lg !== null && lg !== undefined ? [["lg", lg] as [string, number]] : []),
        ...(hasUserDateFilter && from
          ? [["fromDate", from instanceof Date ? from.toJSON() : from] as [string, string]]
          : []),
        ...(hasUserDateFilter && to ? [["toDate", to instanceof Date ? to.toJSON() : to] as [string, string]] : []),
      ],
    }),
    staleTime: STALE_TIME_ONE_HOUR,
  });

  const { items, remaining } = data ?? { remaining: 0, items: [] };

  const onView = useCallback(() => {
    if (isPerformerOrGrouping(appContext)) {
      // not checking for existence of data cause performer passes it on init
      storePageEvent(
        PE_TYPE.LOGGING,
        PE_KEYS.VIEWED_LAST_MINUTE_EVENTS_PERFORMER_PAGE,
        topLevelCategoryId === null ? undefined : String(topLevelCategoryId),
      );
    }
  }, [appContext, topLevelCategoryId, storePageEvent]);

  // MHomeReduceEventCardSize (23424): deployed variant targeted mobile client apps only.
  const reduceEventCardSizes = useIsMobileClientApp();

  const renderOptions = useMemo(() => {
    const opts: {
      size: string;
      hidePaginationButtons: boolean;
      carouselId: HOME_CAROUSEL_IDS;
      analyticsCarouselId: CarouselId;
      analyticsItemType: CarouselItemType;
      gapPx: number;
      imageHeightHint?: number;
      useFlex: boolean;
    } = {
      size: reduceEventCardSizes ? CAROUSEL_SIZES.medium4x3 : carouselSize,
      hidePaginationButtons: true,
      carouselId: HOME_CAROUSEL_IDS.dontMissEvents,
      analyticsCarouselId: CarouselId.DontMissEvents,
      analyticsItemType: CarouselItemType.Event,
      gapPx,
      useFlex: isBaseCardRefactor,
    };

    if (!isMobile) {
      opts.hidePaginationButtons = false;
      opts.imageHeightHint = ImageSize.GridCardBaseDefaultImage.height;
    }
    return opts;
  }, [isMobile, reduceEventCardSizes, carouselSize, gapPx, isBaseCardRefactor]);

  const renderView = useMemo(
    () => (!pending && !error ? renderWithResponsiveCarousel(renderOptions) : () => null),
    [renderOptions, pending, error],
  );

  const imageSizeValue = reduceEventCardSizes ? ImageSize.Medium4x3 : imageSize;
  const imageProps = useMemo(() => ({ size: imageSizeValue }), [imageSizeValue]);
  const useImprovedSkeleton = useSimpleFeatureTernary(FeatureSetEnum.DiscoveryImprovedLoadingSkeleton, false, true);

  const handleItemClick = useCallback(
    (index: number, eventId: number | undefined) => {
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.LAST_MINUTE_DEALS_CLICKED, `${index}-${eventId}-${appContext.pageId}`);
    },
    [storePageEvent, appContext.pageId],
  );

  const renderItem = useCallback(
    (item: EventGridItem, index: number): ReactNode => (
      <Feature
        id={FeatureSetEnum.UseGridCardBaseComponentRefactor}
        control={() =>
          renderCommonEventCard(item, {
            imageProps,
            index,
            size,
            onClick: () => {
              handleItemClick(index, item.eventId);
            },
          })
        }
        variant={() => {
          const priceDrop =
            item.priceDrop?.priceDropPercent !== null &&
            item.priceDrop?.priceDropPercent !== undefined &&
            item.priceDrop?.priceChangePeriodMinutes !== null &&
            item.priceDrop?.priceChangePeriodMinutes !== undefined
              ? {
                  priceDropPercent: item.priceDrop.priceDropPercent,
                  priceChangePeriodMinutes: item.priceDrop.priceChangePeriodMinutes,
                }
              : null;
          return (
            <GridCardBase
              title={item.eventName ?? ""}
              eventDate={item.localEventDate ?? undefined}
              eventTime={item.localEventTime ?? undefined}
              imageUrl={item.imageUrl ?? ""}
              url={item.url ?? ""}
              entityId={item.eventId}
              entityType={ENTITY_TYPES.EVENT}
              size={isMobile ? "mobile" : "default"}
              event={item}
              venueName={item.venueName ?? ""}
              priceDrop={priceDrop}
              formattedFromPrice={item.formattedFromPrice ?? undefined}
              onClick={() => {
                handleItemClick(index, item.eventId);
              }}
            />
          );
        }}
      />
    ),
    [isMobile, imageProps, size, handleItemClick],
  );
  useSyncItemsWithFavoritesContext(items);

  const additionalFetchParams = useMemo(
    () => [
      ...(lt !== null && lt !== undefined ? [["lt", lt] as [string, number]] : []),
      ...(lg !== null && lg !== undefined ? [["lg", lg] as [string, number]] : []),
      ...(from ? [["fromDate", from instanceof Date ? from.toJSON() : from] as [string, string]] : []),
      ...(to ? [["toDate", to instanceof Date ? to.toJSON() : to] as [string, string]] : []),
    ],
    [lt, lg, from, to],
  );

  const imageGridKey = `${topLevelCategoryId ?? 0}-${lt ?? ""}-${lg ?? ""}-${from instanceof Date ? from.toJSON() : (from ?? "")}-${to instanceof Date ? to.toJSON() : (to ?? "")}`;

  if (!pending && items?.length === 0) {
    return null;
  }

  return (
    <DontMissCarouselGridWrapper $removePaddingUsedAsMargin={pageLayoutEnabled} className={className}>
      <DontMissTitleText variant={variant} />
      <InViewTracker onInView={onView} />
      {!pending && !error && (
        <ImageGridManager
          key={imageGridKey}
          method="DontMissEvents"
          selected={topLevelCategoryId ?? 0}
          initItems={items}
          initRemaining={remaining}
          pageSize={pageSize}
          venueId={null}
          headerName=""
          additionalFetchParams={additionalFetchParams}
          renderItem={renderItem}
          renderView={renderView}
          path="/"
        />
      )}
      {pending &&
        (useImprovedSkeleton ? (
          <ResponsiveCarousel
            items={[]}
            size={reduceEventCardSizes ? CAROUSEL_SIZES.medium4x3 : carouselSize}
            hidePaginationButtons={isMobile}
            hasHoverEffect={!isMobile}
            imageHeightHint={imageSizeValue.height}
            isLoading
            loadingCount={isMobile ? 2 : 3}
            renderItem={() => null as never}
          />
        ) : (
          <EmptyCarousel noResult={false} size={isMobile ? 2 : 3} placeHolderLineCount={4} />
        ))}
    </DontMissCarouselGridWrapper>
  );
};
